import React from 'react';

import { cn } from '@/lib/utils';

/**
 * A value you can switch on and off.
 *
 * Outlined when chosen, never filled. A row of solid black chips reads as a
 * toolbar of commands rather than a set of choices that are currently true —
 * and filled-black is reserved, by the same rule that keeps destructive
 * buttons outlined: weight in this interface means "this does something", not
 * "this is on".
 *
 * The border is drawn at every state and only changes colour. Drawing one only
 * when selected, or only on hover, makes it appear from nowhere and shifts
 * every chip beside it by a pixel.
 */
export const FilterChip: React.FC<{
    label: React.ReactNode;
    selected: boolean;
    onClick: () => void;
    /** `sm` for dense filter panels, `md` where the chips are the main control. */
    size?: 'sm' | 'md';
    className?: string;
}> = ({ label, selected, onClick, size = 'sm', className }) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={cn(
            'inline-flex items-center rounded-lg border transition-colors',
            // Matched to the shadcn button scale rather than eyeballed, so a
            // chip and a dropdown standing next to each other are the same
            // height and the same text: `sm` is size="sm", `md` is the default.
            size === 'sm' ? 'h-7 px-2.5 text-[0.8rem]' : 'h-8 px-2.5 text-sm',
            selected
                ? 'border-foreground/40 font-medium text-foreground'
                : 'border-border/60 text-muted-foreground hover:border-border hover:text-foreground',
            className,
        )}
    >
        {label}
    </button>
);
